import { useState } from 'react';
import { Menu, X, Info, Newspaper, Home, UserRound, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';

type NavbarProps = {
  onStatusClick: () => void;
};

const Navbar = ({ onStatusClick }: NavbarProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const enedaLogo =
    'https://enedastorage01.blob.core.windows.net/eneda-assets/eneda-logo-v1.png';

  return (
    <nav className="flex w-full justify-center bg-transparent py-4 font-sans">
      <div className="w-full max-w-6xl px-4">
        <div className="flex items-center rounded-full bg-white px-6 py-2 shadow-md">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <Link to="/">
              <img src={enedaLogo} alt="ENEDA Logo" className="h-10" />
            </Link>
          </div>

          {/* Centered Nav Links */}
          <div className="hidden flex-1 items-center justify-center gap-4 text-sm font-medium text-primary md:flex">
            <Link
              to="/"
              className="flex items-center gap-1 rounded-full px-3 py-2 transition hover:bg-primary hover:text-white"
            >
              <Home className="h-4 w-4" /> Home
            </Link>
            <Link
              to="/news"
              className="flex items-center gap-1 rounded-full px-3 py-2 transition hover:bg-primary hover:text-white"
            >
              <Newspaper className="h-4 w-4" /> News
            </Link>
            <Link
              to="/about"
              className="flex items-center gap-1 rounded-full px-3 py-2 transition hover:bg-primary hover:text-white"
            >
              <Info className="h-4 w-4" /> About
            </Link>
          </div>

          {/* Right Side Buttons */}
          <div className="ml-auto hidden items-center gap-3 md:flex">
            {/* Status button (desktop) */}
            <button
              onClick={onStatusClick}
              className="rounded-full bg-white px-4 py-2 text-sm font-medium text-primary shadow hover:bg-gray-100"
            >
              Status
            </button>

            {/* Book Appointment button (desktop) */}
            <Link
              to="/book-appointment"
              className="flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-medium text-primary shadow hover:bg-gray-100"
            >
              <Calendar className="h-4 w-4" />
              Set Appointment
            </Link>

            <Link
              to="/membership"
              className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
            >
              Become a Member!
            </Link>
            <div className="flex justify-center">
              <a
                href="https://admin.eneda-ph.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-primary text-primary transition hover:bg-primary hover:text-white"
              >
                <UserRound className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="ml-auto md:hidden">
            <button onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Nav Dropdown */}
        {isOpen ? (
          <div className="mt-2 space-y-2 rounded-xl bg-white px-4 py-3 text-sm text-gray-700 shadow md:hidden">
            <Link
              to="/"
              className="flex items-center gap-2 rounded-full px-3 py-2 hover:bg-primary hover:text-white"
            >
              <Home className="h-4 w-4" /> Home
            </Link>
            <Link
              to="/news"
              className="flex items-center gap-2 rounded-full px-3 py-2 hover:bg-primary hover:text-white"
            >
              <Newspaper className="h-4 w-4" /> News
            </Link>
            <Link
              to="/about"
              className="flex items-center gap-2 rounded-full px-3 py-2 hover:bg-primary hover:text-white"
            >
              <Info className="h-4 w-4" /> About
            </Link>

            {/* Status button (mobile) */}
            <button
              onClick={() => {
                setIsOpen(false); // close menu first
                onStatusClick();
              }}
              className="block w-full rounded-full bg-white px-4 py-2 text-center text-primary shadow hover:bg-gray-100"
            >
              Status
            </button>

            {/* Book Appointment button (mobile) */}
            <Link
              to="/book-appointment"
              onClick={() => setIsOpen(false)}
              className="flex items-center justify-center gap-2 rounded-full bg-white px-4 py-2 text-primary shadow hover:bg-gray-100"
            >
              <Calendar className="h-4 w-4" />
              Set Appointment
            </Link>

            <Link
              to="/membership"
              className="block rounded-full bg-primary px-4 py-2 text-center font-semibold text-white hover:opacity-90"
            >
              Become a Member!
            </Link>

            {/* External redirect */}
            <a
              href="https://admin.eneda-ph.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-primary text-primary transition hover:bg-primary hover:text-white"
            >
              <UserRound className="h-5 w-5" />
            </a>
          </div>
        ) : null}
      </div>
    </nav>
  );
};

export default Navbar;
